import axios, { AxiosError } from 'axios';

// Vite importMeta typing shim (for environments where 'import.meta.env' isn't typed)
declare const importMeta: { env?: Record<string, unknown> };
import type {
  ShareValidationRequest,
  ShareValidationResponse,
  ReportData,
  ApiError,
  ClinicalFinding,
  ClinicalFindingRequest,
  FollowUpRequestResponse,
  ClinicianSurveyRequest,
  ClinicianSurveyStatus,
  CheckInData
} from '../types';

const getEnvString = (key: string): string | undefined => {
  const viteEnvValue = typeof import.meta !== 'undefined'
    ? (import.meta as any).env?.[key]
    : undefined;
  const shimEnvValue = typeof importMeta !== 'undefined'
    ? importMeta.env?.[key]
    : undefined;
  const value = typeof viteEnvValue === 'string' ? viteEnvValue : shimEnvValue;
  return typeof value === 'string' ? value.trim() || undefined : undefined;
};

// Resolve API base URL
export const API_BASE_URL = (() => {
  const envUrl = getEnvString('VITE_API_URL');
  const envIsAbsolute = Boolean(envUrl && /^https?:\/\//i.test(envUrl));

  if (typeof window !== 'undefined') {
    const { hostname } = window.location;
    const isLocalHost = hostname === 'localhost'
      || hostname === '127.0.0.1'
      || hostname === '0.0.0.0'
      || hostname === '::1';

    // A production-mode build is also served by the local API during QA.
    // Keep those requests same-origin even when VITE_API_URL is absolute.
    if (isLocalHost) {
      return '/api';
    }

    if (hostname === 'viewer.mamabearhealthapp.com' || hostname === 'www.viewer.mamabearhealthapp.com') {
      return 'https://mamabearhealthapp.com/api';
    }
  }

  if (envIsAbsolute && envUrl) {
    return envUrl;
  }

  return envUrl || '/api';
})();

export const DEFAULT_FHIR_EHR_BASE_URL = (() => {
  return getEnvString('VITE_FHIR_EHR_BASE_URL') || 'https://fhir.epic.com/interconnect-fhir-oauth/api/FHIR/R4';
})();

const FHIR_AUTH_TOKEN_KEYS = [
  'fhirAuthToken',
  'mamabearAuthToken',
  'mamaBearAuthToken',
  'authToken',
  'accessToken',
  'pasetoToken',
];

const extractToken = (value: string | null): string | null => {
  const trimmed = value?.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed) as Record<string, unknown>;
      const candidate = parsed.accessToken || parsed.token || parsed.authToken || parsed.pasetoToken;
      return typeof candidate === 'string' && candidate.trim() ? candidate.trim() : null;
    } catch {
      return null;
    }
  }

  return trimmed;
};

export const getFhirAuthToken = (): string | null => {
  if (typeof window === 'undefined') return null;

  for (const key of FHIR_AUTH_TOKEN_KEYS) {
    const sessionToken = extractToken(window.sessionStorage.getItem(key));
    if (sessionToken) return sessionToken;

    const localToken = extractToken(window.localStorage.getItem(key));
    if (localToken) return localToken;
  }

  return null;
};

export const setFhirAuthToken = (token: string | null): void => {
  if (typeof window === 'undefined') return;

  if (token?.trim()) {
    window.sessionStorage.setItem('fhirAuthToken', token.trim());
    return;
  }

  window.sessionStorage.removeItem('fhirAuthToken');
};

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include cookies for session management
});

// Request interceptor to add auth token if available
apiClient.interceptors.request.use(
  (config) => {
    const requestUrl = config.url ?? '';
    const normalizedRequestUrl = requestUrl.toLowerCase();
    const isFhirEndpoint = normalizedRequestUrl.includes('/fhirauth') ||
      normalizedRequestUrl.includes('/consents/fhir');
    const token = sessionStorage.getItem('shareToken');
    const fhirAuthToken = isFhirEndpoint ? getFhirAuthToken() : null;
    // Do NOT attach Authorization for the anonymous validation endpoint
    const isValidateEndpoint = typeof config.url === 'string' &&
      config.method?.toLowerCase() === 'post' &&
      (config.url.endsWith('/share/validate') || config.url === '/share/validate');

    if (isFhirEndpoint && fhirAuthToken) {
      config.headers.Authorization = `Bearer ${fhirAuthToken}`;
    } else if (!isValidateEndpoint && !isFhirEndpoint && token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    const requestUrl = error.config?.url ?? '';
    const normalizedRequestUrl = requestUrl.toLowerCase();
    const isFhirEndpoint = normalizedRequestUrl.includes('/fhirauth') ||
      normalizedRequestUrl.includes('/consents/fhir');

    if (error.response?.status === 401 && !isFhirEndpoint) {
      // Clear session and redirect to password page
      sessionStorage.removeItem('shareToken');
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

/**
 * Validates share access with password
 */
export const validateShareAccess = async (
  request: ShareValidationRequest
): Promise<ShareValidationResponse> => {
  try {
    const response = await apiClient.post<ShareValidationResponse>(
      '/share/validate',
      request
    );

    // Store token if validation successful
    if (response.data.isValid && response.data.shareId) {
      // Keep token only for the current tab session
      sessionStorage.setItem('shareToken', response.data.sessionToken || response.data.shareId);
    }

    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError<ApiError>;
    throw new Error(
      axiosError.response?.data?.message || 'We could not verify access to this report. Please try again.'
    );
  }
};

/**
 * Fetches report data for a validated share
 */
export const fetchReportData = async (shareId: string): Promise<ReportData> => {
  try {
    const response = await apiClient.get<ReportData>(`/share/${shareId}/report`);
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError<ApiError>;
    throw new Error(
      axiosError.response?.data?.message || 'We could not load this report. Please try again.'
    );
  }
};

/**
 * Fetches the chain of related check-ins (sorted by date) for a validated share.
 * Returns an empty array if the share has no chain or the endpoint is unavailable —
 * callers should fall back to the single-check-in flow in that case.
 */
export const fetchChainData = async (shareId: string): Promise<CheckInData[]> => {
  const response = await apiClient.get<{ eCheckChainId?: string; chainData?: CheckInData[] } | CheckInData[]>(`/share/${shareId}/chain`);
  // Backend returns { eCheckChainId, chainData: [...] }
  if (Array.isArray(response.data)) return response.data;
  const wrapped = response.data as { chainData?: CheckInData[] };
  return Array.isArray(wrapped.chainData) ? wrapped.chainData : [];
};

/**
 * Downloads the PDF report
 */
export const downloadReport = async (shareId: string): Promise<Blob> => {
  try {
    const response = await apiClient.get(`/share/${shareId}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError<ApiError>;
    throw new Error(
      axiosError.response?.data?.message || 'We could not download this report. Please try again.'
    );
  }
};

/**
 * Logs share access activity
 */
export const logShareAccess = async (
  shareId: string,
  action: string
): Promise<void> => {
  try {
    await apiClient.post(`/share/${shareId}/log`, {
      action,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
    });
  } catch (error) {
    // Don't throw on logging errors, just console log
    console.error('Failed to log share access:', error);
  }
};

/**
 * Checks if a share session is still valid
 */
export const checkShareSession = async (shareId: string): Promise<boolean> => {
  try {
    const response = await apiClient.get(`/share/${shareId}/session`);
    return response.data.isValid;
  } catch (error) {
    return false;
  }
};

/**
 * Extends share session
 */
export const extendShareSession = async (shareId: string): Promise<void> => {
  try {
    await apiClient.post(`/share/${shareId}/session/extend`);
  } catch (error) {
    const axiosError = error as AxiosError<ApiError>;
    throw new Error(
      axiosError.response?.data?.message || 'We could not extend your session. Please reopen the report.'
    );
  }
};

/**
 * Creates or updates provider-authored clinical findings for a report.
 */
export const saveClinicalFinding = async (
  shareId: string,
  request: ClinicalFindingRequest
): Promise<{ clinicalFinding: ClinicalFinding }> => {
  try {
    const response = await apiClient.put<{ clinicalFinding: ClinicalFinding }>(
      `/share/${shareId}/clinical-finding`,
      request
    );
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError<ApiError>;
    throw new Error(
      axiosError.response?.data?.message || 'We could not save the clinical findings. Please try again.'
    );
  }
};

/**
 * Deletes provider-authored clinical findings for a report.
 */
export const deleteClinicalFinding = async (shareId: string): Promise<void> => {
  try {
    await apiClient.delete(`/share/${shareId}/clinical-finding`);
  } catch (error) {
    const axiosError = error as AxiosError<ApiError>;
    throw new Error(
      axiosError.response?.data?.message || 'We could not delete the clinical findings. Please try again.'
    );
  }
};

/**
 * Texts the parent from the provider viewer to request another follow-up.
 */
export const requestParentFollowUp = async (shareId: string): Promise<FollowUpRequestResponse> => {
  try {
    const response = await apiClient.post<FollowUpRequestResponse>(`/share/${shareId}/request-follow-up`);
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError<ApiError>;
    throw new Error(
      axiosError.response?.data?.message || 'We could not send the text to the parent. Please try again.'
    );
  }
};

/**
 * FHIR API Response types
 */
export interface SaveToMyChartResponse {
  success: boolean;
  documentId?: string;
  reportUrl?: string;
  accessCode?: string;
  myChartUrl?: string;
  myChartDeepLink?: string;
  preparedMessage?: string;
  message?: string;
  instructions?: string[];
}

export interface SaveToMyChartRequest {
  reportUrl: string;
  reportTitle?: string;
  reportDate?: string;
  description?: string;
  patientId?: string;
  accessCode?: string;
}

export interface FhirSessionResponse {
  hasToken: boolean;
  expiresAt?: string;
  patientId?: string;
}

export interface FhirConsentStatusResponse {
  active: boolean;
  expiresAt?: string;
  message?: string;
}

export interface FhirAuthorizeUrlResponse {
  authorizeUrl: string;
  url?: string;
  state?: string;
  codeVerifier?: string;
  instructions?: string[];
}

export interface FhirPatient {
  id: string;
  name?: string;
  displayName?: string;
  birthDate?: string;
  gender?: string;
  [key: string]: unknown;
}

export interface FhirProvider {
  id: string;
  name?: string;
  displayName?: string;
  role?: string;
  specialty?: string;
  organization?: string;
  [key: string]: unknown;
}

export interface CheckInShareInfoResponse {
  shareId: string;
  checkInId: string;
  shareLink?: string;
  reportUrl?: string;
  accessCode?: string;
  myChartUrl?: string;
  myChartDeepLink?: string;
  preparedMessage?: string;
  subject?: string;
  content?: string;
  childName?: string;
  checkInDate?: string;
  createdAt: string;
  expiresAt?: string;
  isExpired: boolean;
  isReportReady: boolean;
  readinessStatus?: string;
  retryAfterSeconds: number;
}

export interface ShareCheckInToProviderRequest {
  checkInId: string;
  providerRoleId: string;
  patientId?: string;
  patientName?: string;
  providerName?: string;
  reportTitle?: string;
  description?: string;
  subject?: string;
  content?: string;
}

export interface CheckInShareMessageRequest {
  checkInId: string;
}

export interface ShareCheckInToProviderResponse {
  success: boolean;
  documentCreated: boolean;
  messageSent: boolean;
  documentId?: string;
  shareLink?: string;
  reportUrl?: string;
  accessCode?: string;
  myChartUrl?: string;
  myChartDeepLink?: string;
  preparedMessage?: string;
  providerName?: string;
  checkInId: string;
  childName?: string;
  checkInDate?: string;
  deliveryStatus?: string;
  sentAt?: string;
  isReportReady: boolean;
  readinessStatus?: string;
  message?: string;
}

/**
 * Attempts the optional Epic DocumentReference filing workflow.
 * Public Epic sandbox apps may return success=false when this write API is not enabled.
 */
export const saveToMyChart = async (
  request: SaveToMyChartRequest
): Promise<SaveToMyChartResponse> => {
  try {
    const response = await apiClient.post<SaveToMyChartResponse>(
      '/FhirAuth/save-report',
      request
    );
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError<ApiError>;
    
    // Handle 401 - need to authenticate with Epic
    if (axiosError.response?.status === 401) {
      throw new Error('NEEDS_AUTH');
    }
    
    throw new Error(
      axiosError.response?.data?.message || 'We could not file this report in Epic. Please try again.'
    );
  }
};

/**
 * Initiates Epic OAuth flow for FHIR access
 */
export const initiateFhirAuth = async (
  ehrBaseUrl: string = DEFAULT_FHIR_EHR_BASE_URL,
  returnUrl: string = typeof window !== 'undefined' ? window.location.href : ''
): Promise<void> => {
  const authResponse = await getFhirAuthorizeUrl(ehrBaseUrl, returnUrl);
  const authorizeUrl = authResponse.authorizeUrl || authResponse.url;
  if (!authorizeUrl) {
    throw new Error('We could not connect to Epic. Please try again.');
  }
  window.location.href = authorizeUrl;
};

/**
 * Gets the Epic OAuth URL without redirecting. Useful for native/web clients that
 * need to open an external browser and keep local state.
 */
export const getFhirAuthorizeUrl = async (
  ehrBaseUrl: string = DEFAULT_FHIR_EHR_BASE_URL,
  returnUrl: string = typeof window !== 'undefined' ? window.location.href : ''
): Promise<FhirAuthorizeUrlResponse> => {
  const response = await apiClient.get<FhirAuthorizeUrlResponse>(
    '/FhirAuth/authorize-url',
    { params: { ehrBaseUrl, returnUrl: returnUrl || undefined } }
  );
  return response.data;
};

export const getFhirConsentStatus = async (): Promise<FhirConsentStatusResponse> => {
  const response = await apiClient.get<FhirConsentStatusResponse>('/Consents/fhir/status');
  return response.data;
};

export const createOrRenewFhirConsent = async (): Promise<FhirConsentStatusResponse> => {
  const response = await apiClient.post<FhirConsentStatusResponse>('/Consents/fhir');
  return response.data;
};

/**
 * Checks if user has valid FHIR/Epic session
 */
export const checkFhirSession = async (): Promise<FhirSessionResponse> => {
  try {
    const response = await apiClient.get('/FhirAuth/me');
    return response.data;
  } catch {
    return { hasToken: false };
  }
};

export const getFhirPatients = async (): Promise<FhirPatient[]> => {
  const response = await apiClient.get<FhirPatient[]>('/FhirAuth/patients');
  return response.data;
};

export const getFhirProviders = async (patientId?: string): Promise<FhirProvider[]> => {
  const response = await apiClient.get<FhirProvider[]>('/FhirAuth/providers', {
    params: patientId ? { patientId } : undefined,
  });
  return response.data;
};

export const getCheckInShareInfo = async (checkInId: string): Promise<CheckInShareInfoResponse> => {
  const response = await apiClient.get<CheckInShareInfoResponse>(
    `/FhirAuth/checkin/${encodeURIComponent(checkInId)}/share-info`
  );
  return response.data;
};

export const getCheckInShareMessage = async (
  checkInId: string
): Promise<CheckInShareInfoResponse> => {
  const response = await apiClient.post<CheckInShareInfoResponse>(
    '/FhirAuth/checkin/share-message',
    { checkInId }
  );
  return response.data;
};

export const shareCheckInToProvider = async (
  request: ShareCheckInToProviderRequest
): Promise<ShareCheckInToProviderResponse> => {
  const response = await apiClient.post<ShareCheckInToProviderResponse>(
    '/FhirAuth/share-checkin-to-provider',
    request
  );
  return response.data;
};

/**
 * Submits a clinician survey response
 */
export const submitClinicianSurvey = async (
  shareId: string,
  survey: ClinicianSurveyRequest
): Promise<{ surveyId: string; message: string; isComplete: boolean }> => {
  try {
    const response = await apiClient.post(`/share/${shareId}/clinician-survey`, survey);
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError<ApiError>;
    throw new Error(
      axiosError.response?.data?.message || 'We could not submit the survey. Please try again.'
    );
  }
};

/**
 * Gets the clinician survey status for a share
 */
export const getClinicianSurveyStatus = async (shareId: string): Promise<ClinicianSurveyStatus> => {
  try {
    const response = await apiClient.get<ClinicianSurveyStatus>(`/share/${shareId}/clinician-survey`);
    return response.data;
  } catch (error) {
    // Return default status on error
    return {
      exists: false,
      isComplete: false,
      dismissedForLater: false,
      dismissCount: 0
    };
  }
};

export default {
  validateShareAccess,
  fetchReportData,
  fetchChainData,
  downloadReport,
  logShareAccess,
  checkShareSession,
  extendShareSession,
  saveClinicalFinding,
  deleteClinicalFinding,
  requestParentFollowUp,
  getFhirAuthToken,
  setFhirAuthToken,
  saveToMyChart,
  initiateFhirAuth,
  getFhirAuthorizeUrl,
  getFhirConsentStatus,
  createOrRenewFhirConsent,
  checkFhirSession,
  getFhirPatients,
  getFhirProviders,
  getCheckInShareInfo,
  getCheckInShareMessage,
  shareCheckInToProvider,
  submitClinicianSurvey,
  getClinicianSurveyStatus,
};
